import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import LeadCaptureForm from '../components/LeadCaptureForm';
import SplashNav from '../components/SplashNav';
import {
  Search, Zap, Shield, Globe, BarChart3, ArrowRight, Sparkles,
  Workflow, Clock, RefreshCw, Check, ChevronLeft, ChevronRight,
  Calendar, Mail, Image, Rocket, Cpu, GitBranch, Play, Zap as ZapIcon,
  TrendingUp, AlertCircle, Settings,
} from 'lucide-react';

const slides = [
  {
    image: 'https://images.pexels.com/photos/38984789/pexels-photo-38984789.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Automate the Entire Pipeline',
    desc: 'Set up workflows that run research, generation, compliance, and publishing on autopilot.',
    icon: Workflow,
  },
  {
    image: 'https://images.pexels.com/photos/32026165/pexels-photo-32026165.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Your Command Center',
    desc: 'Monitor every pipeline stage in real-time — from trigger to deployment — with full visibility.',
    icon: Cpu,
  },
  {
    image: 'https://images.pexels.com/photos/577195/pexels-photo-577195.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Data-Driven Decisions',
    desc: 'Every run feeds analytics back into the system, so each campaign performs better than the last.',
    icon: TrendingUp,
  },
  {
    image: 'https://images.pexels.com/photos/3582392/pexels-photo-3582392.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Total Control',
    desc: 'Schedule pipelines hourly, daily, weekly, or monthly — or fire them off manually with one click.',
    icon: Settings,
  },
];

const pipelineSteps = [
  { step: '01', icon: Search, title: 'Research', desc: 'AI-powered topic research with keyword clusters, competitor mapping, and trend analysis.', color: 'from-blue-500 to-cyan-600' },
  { step: '02', icon: Zap, title: 'Generate', desc: 'Create e-books, emails, ad copy, social posts, SEO metadata — all in parallel.', color: 'from-violet-500 to-purple-600' },
  { step: '03', icon: Image, title: 'Image Creation', desc: 'AI-generated marketing visuals in 10+ styles and multiple dimensions.', color: 'from-amber-500 to-orange-600' },
  { step: '04', icon: Shield, title: 'Verify', desc: 'Automatic compliance scanning and plagiarism detection via Quetext.', color: 'from-emerald-500 to-teal-600' },
  { step: '05', icon: Globe, title: 'Publish', desc: 'Auto-deploy to connected platforms — websites, CMS, social, email, RSS.', color: 'from-rose-500 to-pink-600' },
  { step: '06', icon: Calendar, title: 'Social Auto-Post', desc: 'Schedule generated social posts across LinkedIn, Facebook, Instagram.', color: 'from-indigo-500 to-blue-600' },
  { step: '07', icon: Mail, title: 'Email Sequence', desc: 'Trigger automated email sequences to subscribers via Resend API.', color: 'from-cyan-500 to-blue-600' },
];

const scheduleOptions = [
  { icon: Play, label: 'Manual', desc: 'Trigger pipelines on demand with one click' },
  { icon: Clock, label: 'Hourly', desc: 'Run every hour for high-frequency content needs' },
  { icon: Calendar, label: 'Daily', desc: 'Daily content generation and publishing cycles' },
  { icon: RefreshCw, label: 'Weekly / Monthly', desc: 'Long-form campaigns on a recurring schedule' },
];

const features = [
  { icon: GitBranch, title: '7-Step Pipeline Chain', desc: 'Research, generate, image, cover, verify, publish, and email — all chained in sequence.' },
  { icon: Cpu, title: 'Edge Function Orchestration', desc: 'The automation-runner coordinates multiple edge functions in a reliable sequence.' },
  { icon: Clock, title: 'Flexible Scheduling', desc: 'Manual, hourly, daily, weekly, or monthly intervals — switch anytime.' },
  { icon: AlertCircle, title: 'Per-Step Status Tracking', desc: 'Every step tracked: pending, running, completed, skipped, or failed.' },
  { icon: RefreshCw, title: 'Run History', desc: 'Full audit trail of every pipeline run with detailed step results.' },
  { icon: Settings, title: 'Pre-Filled Config', desc: 'Topic and keywords auto-populated from your business profile.' },
];

const SplashAutomation: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <div className="min-h-screen bg-slate-100">
      <SplashNav />
      {/* Page Header */}
      <section className="bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-600 px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-4">
            <Workflow className="h-3.5 w-3.5 text-white" />
            <span className="text-xs font-mono text-white/80 tracking-wider">AURELIAN AUTOMATION ENGINE</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">The Automation Engine</h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">Automate your entire content marketing pipeline — research, generate, verify, publish, and email — on schedule or on demand, with zero manual work.</p>
        </div>
      </section>
      {/* Hero Slideshow */}
      <section className="relative h-[600px] md:h-[680px] overflow-hidden">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-blue-900/75 to-cyan-900/80" />
            <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
              <div
                className={`max-w-2xl transition-all duration-700 ${
                  i === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                  <slide.icon className="h-3.5 w-3.5 text-cyan-300" />
                  <span className="text-xs font-mono text-white/70 tracking-wider">
                    {String(i + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                  </span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
                  {slide.title}
                </h2>
                <p className="text-base md:text-lg text-white/70 mb-8 leading-relaxed max-w-xl">
                  {slide.desc}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                >
                  <Workflow className="h-4 w-4" />
                  Build Your First Pipeline
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentSlide ? 'w-8 bg-cyan-400' : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Pipeline Visualization */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <Workflow className="h-3.5 w-3.5 text-blue-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">PIPELINE ARCHITECTURE</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">7 Stages, Zero Manual Work</h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">Each pipeline chains these steps together — configure once, run forever.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pipelineSteps.map((step, i) => (
            <div key={step.step} className="relative">
              <div className="glass-card glass-card-hover p-5 h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-md`}>
                    <step.icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-[10px] font-mono text-slate-400">{step.step}</span>
                </div>
                <h3 className="text-sm font-bold text-slate-800 mb-1">{step.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
              {i < pipelineSteps.length - 1 && i % 4 !== 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-px bg-slate-200" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Schedule Options */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Run On Your Schedule</h2>
            <p className="text-sm text-slate-400 max-w-xl mx-auto">Choose how often your pipelines fire — change it anytime.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {scheduleOptions.map((opt) => (
              <div key={opt.label} className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-cyan-500/30 transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-3 shadow-md">
                  <opt.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{opt.label}</h3>
                <p className="text-xs text-slate-400">{opt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">Built for Reliability</h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">Every pipeline run is tracked, logged, and optimized for performance.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div key={f.title} className="glass-card p-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 shadow-md">
                <f.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-2">{f.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="glass-card p-8 md:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">How It Works</h2>
            <p className="text-sm text-slate-400">Three steps to automated content marketing</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: '01', icon: Settings, title: 'Configure', desc: 'Set your topic, keywords, content types, and schedule. Pre-filled from your business profile.' },
              { num: '02', icon: ZapIcon, title: 'Run', desc: 'The automation-runner orchestrates all 7 steps by calling the right edge functions in sequence.' },
              { num: '03', icon: Check, title: 'Deploy', desc: 'Content is generated, verified, published, and emailed — all automatically. Track every step.' },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/20">
                  <step.icon className="h-7 w-7 text-white" />
                </div>
                <p className="text-[10px] font-mono text-cyan-500 mb-1">{step.num}</p>
                <h3 className="text-sm font-bold text-slate-800 mb-2">{step.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
              <Rocket className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-xs font-mono text-slate-500 tracking-wider">AUTOMATION ACCESS</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-3">Automate Your Marketing Forever</h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">Set up your first automation pipeline and let the system handle research, content, and publishing on autopilot.</p>
          </div>
          <LeadCaptureForm
            pageSource="automation"
            title="Start Automating Today"
            subtitle="Tell us about your automation goals and we'll set up your first pipeline"
            accentColor="blue"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Workflow className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-white">Aurelian Automation Engine</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="text-xs text-slate-400 hover:text-white transition-colors">Sign In</Link>
            <Link to="/pricing" className="text-xs text-slate-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/legal" className="text-xs text-slate-400 hover:text-white transition-colors">Legal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashAutomation;
